class JewelryBoxAddon extends HTMLElement {
  constructor() {
    super();
    console.log('✓ JewelryBoxAddon initialized');
    this.product = null;
    this.selectedOptions = {};
    this.selectedVariant = null;
    this.shippingKit = 'Standard Kit';
    this.bouquetDate = '';
    this.colorCorrection = 'No';
    this.hasRealOptions = false;
    this.init();
  }

  init() {
    // Parse product data from the JSON script tags
    this.parseProductData();

    // Check if product has real options (not just default variant)
    this.checkForRealOptions();

    // Set up mobile accordion behavior
    this.setupMobileAccordion();

    // Set up variant option selectors (only if has real options)
    if (this.hasRealOptions) {
      this.setupVariantOptions();
    } else {
      // Use the first/default variant
      this.selectedVariant = this.product?.variants?.[0] || null;
    }

    // Set up shipping kit selector
    this.setupShippingKit();

    // Set up custom text fields
    this.setupCustomFields();

    // Set up add to cart
    this.setupAddToCart();

    // Show first step
    this.showStep(1);

    // Initial variant selection and summary
    if (this.hasRealOptions) {
      this.updateSelectedVariant();
    } else {
      this.updateSummary();
    }
  }

  checkForRealOptions() {
    if (!this.product) return;

    // Check if product has only one variant with "Default Title"
    if (this.product.variants?.length === 1 &&
        this.product.variants[0].title === 'Default Title') {
      this.hasRealOptions = false;
      console.log('✓ Product has only default variant, skipping option selectors');
    } else if (this.product.options?.length > 1 ||
               (this.product.options?.length === 1 && this.product.options[0] !== 'Title')) {
      this.hasRealOptions = true;
      console.log('✓ Product has real options');
    } else {
      this.hasRealOptions = false;
    }
  }

  parseProductData() {
    // Parse main product data
    const productDataScript = document.getElementById('jewelry-box-product-data');
    if (productDataScript) {
      try {
        this.product = JSON.parse(productDataScript.textContent);
        console.log('✓ Parsed product:', this.product.title);
        console.log('✓ Variants:', this.product.variants?.length || 0);
        console.log('✓ Options:', this.product.options || []);
      } catch (e) {
        console.error('✗ Failed to parse product data:', e);
      }
    } else {
      console.error('✗ No JSON script tag found with id="jewelry-box-product-data"');
    }

    // Parse metaobjects data (shipping kit variant ID)
    const metaobjectsDataScript = document.getElementById('jewelry-box-metaobjects-data');
    if (metaobjectsDataScript) {
      try {
        this.metaobjectsData = JSON.parse(metaobjectsDataScript.textContent);
        console.log('✓ Parsed metaobjects data:', this.metaobjectsData);
      } catch (e) {
        console.error('✗ Failed to parse metaobjects data:', e);
      }
    } else {
      console.warn('⚠ No JSON script tag found with id="jewelry-box-metaobjects-data"');
    }
  }

  setupMobileAccordion() {
    console.log('→ Setting up accordion');

    const steps = this.querySelectorAll('.jewelry-box-step');

    steps.forEach((step) => {
      const title = step.querySelector('.step-title');
      if (!title) return;

      title.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleAccordion(step);
      });
    });

    // Open first step by default
    const firstStep = this.querySelector('.jewelry-box-step');
    if (firstStep) {
      firstStep.classList.add('is-open');
    }

    console.log('✓ Accordion set up complete');
  }

  toggleAccordion(step) {
    const isOpen = step.classList.contains('is-open');

    // Close all other steps
    this.querySelectorAll('.jewelry-box-step.is-open').forEach(openStep => {
      if (openStep !== step) {
        openStep.classList.remove('is-open');
      }
    });

    // Toggle current step
    if (isOpen) {
      step.classList.remove('is-open');
    } else {
      step.classList.add('is-open');
    }
  }

  markStepCompleted(stepNumber) {
    const step = this.querySelector(`[js-step="${stepNumber}"]`);
    if (step) {
      step.classList.add('is-completed');
    }
  }

  openNextStep(currentStepNumber) {
    this.markStepCompleted(currentStepNumber);

    const nextStep = this.querySelector(`[js-step="${currentStepNumber + 1}"]`);
    if (nextStep) {
      const currentStep = this.querySelector(`[js-step="${currentStepNumber}"]`);
      if (currentStep) {
        currentStep.classList.remove('is-open');
      }

      setTimeout(() => {
        nextStep.classList.add('is-open');
      }, 200);
    }
  }

  setupVariantOptions() {
    console.log('→ Setting up variant options');

    const optionSelectors = this.querySelectorAll('[data-option-index]');

    optionSelectors.forEach((selector) => {
      const optionIndex = parseInt(selector.dataset.optionIndex);

      const radios = selector.querySelectorAll('input[type="radio"]');

      radios.forEach(radio => {
        // Initialize selectedOptions with first checked values
        if (radio.checked) {
          this.selectedOptions[`option${optionIndex + 1}`] = radio.value;
        }

        // Add change listener
        radio.addEventListener('change', () => {
          if (radio.checked) {
            this.handleOptionChange(optionIndex, radio.value);
          }
        });
      });
    });

    // Update availability
    this.updateOptionAvailability();

    // Find initial variant
    this.updateSelectedVariant();

    console.log('✓ Variant options set up complete');
  }

  handleOptionChange(optionIndex, value) {
    this.selectedOptions[`option${optionIndex + 1}`] = value;

    // Update availability of dependent options
    this.updateOptionAvailability();

    // Auto-select first available option in next steps if current selection is unavailable
    for (let i = optionIndex + 1; i < this.product.options.length; i++) {
      this.autoSelectFirstAvailableOption(i);
    }

    // Find matching variant
    this.updateSelectedVariant();

    // Move to next step
    this.showStep(optionIndex + 2);

    // Update summary
    this.updateSummary();
  }

  updateSelectedVariant() {
    if (!this.product || !this.product.variants) {
      console.error('✗ No product or variants available');
      return;
    }

    // Find variant that matches all selected options
    const matchingVariant = this.product.variants.find(variant => {
      return Object.keys(this.selectedOptions).every(key => {
        const optionValue = this.selectedOptions[key];
        return variant[key] === optionValue;
      });
    });

    if (matchingVariant) {
      this.selectedVariant = matchingVariant;
      console.log('✓ Selected variant:', matchingVariant.title, matchingVariant.id);

      // Update preview image
      this.updatePreviewImage(matchingVariant);

      // Update pricing
      this.updateSummary();
    } else {
      this.selectedVariant = null;
      console.warn('⚠ No matching variant found');
    }
  }

  updateOptionAvailability() {
    const optionSelectors = this.querySelectorAll('[data-option-index]');

    optionSelectors.forEach((selector) => {
      const optionIndex = parseInt(selector.dataset.optionIndex);

      // Skip availability checking for first option
      if (optionIndex === 0) {
        return;
      }

      const radios = selector.querySelectorAll('input[type="radio"]');

      radios.forEach(radio => {
        const testOptions = {};

        // Add all options before the current one
        for (let i = 1; i <= optionIndex; i++) {
          if (this.selectedOptions[`option${i}`]) {
            testOptions[`option${i}`] = this.selectedOptions[`option${i}`];
          }
        }

        // Add the current option being tested
        testOptions[`option${optionIndex + 1}`] = radio.value;

        // Check if any variant exists with these options
        const matchingVariants = this.product.variants.filter(variant => {
          return Object.keys(testOptions).every(key => {
            return variant[key] === testOptions[key];
          });
        });

        const variantExists = matchingVariants.length > 0;
        const hasAvailableVariant = matchingVariants.some(v => v.available);

        const label = radio.closest('.option-item');
        if (label) {
          if (!variantExists) {
            label.style.display = 'none';
            radio.disabled = true;
          } else if (!hasAvailableVariant) {
            label.style.display = '';
            label.classList.add('unavailable');
            radio.disabled = true;
          } else {
            label.style.display = '';
            label.classList.remove('unavailable');
            radio.disabled = false;
          }
        }
      });
    });
  }

  autoSelectFirstAvailableOption(optionIndex) {
    const selector = this.querySelector(`[data-option-index="${optionIndex}"]`);
    if (!selector) return;

    const radios = selector.querySelectorAll('input[type="radio"]');

    const currentlySelected = selector.querySelector('input[type="radio"]:checked');
    const currentLabel = currentlySelected?.closest('.option-item');
    const isCurrentHidden = currentLabel?.style.display === 'none';
    const isCurrentUnavailable = currentLabel?.classList.contains('unavailable');
    const isCurrentAvailable = !isCurrentHidden && !isCurrentUnavailable;

    if (currentlySelected && !isCurrentAvailable) {
      for (const radio of radios) {
        const label = radio.closest('.option-item');
        const isHidden = label?.style.display === 'none';
        const isUnavailable = label?.classList.contains('unavailable');
        if (!isHidden && !isUnavailable) {
          radio.checked = true;
          this.selectedOptions[`option${optionIndex + 1}`] = radio.value;
          return;
        }
      }
    }
  }

  updatePreviewImage(variant) {
    const imageElement = this.querySelector('[js-product-image]');
    const imageContainer = this.querySelector('[js-image]');

    let imageUrl = null;

    if (variant) {
      if (variant.featured_image?.src) {
        imageUrl = variant.featured_image.src;
      } else if (typeof variant.featured_image === 'string' && variant.featured_image) {
        imageUrl = variant.featured_image;
      } else if (variant.image?.src) {
        imageUrl = variant.image.src;
      } else if (typeof variant.image === 'string' && variant.image) {
        imageUrl = variant.image;
      } else if (variant.image_id && this.product.images) {
        const matchingImage = this.product.images.find(img => img.id === variant.image_id);
        if (matchingImage) {
          imageUrl = matchingImage.src || matchingImage;
        }
      }
    }

    // Fall back to product featured image
    if (!imageUrl && this.product.featured_image) {
      if (typeof this.product.featured_image === 'string') {
        imageUrl = this.product.featured_image;
      } else if (this.product.featured_image.src) {
        imageUrl = this.product.featured_image.src;
      }
    }

    // Fall back to first product image
    if (!imageUrl && this.product.images && this.product.images.length > 0) {
      const firstImage = this.product.images[0];
      imageUrl = firstImage.src || firstImage;
    }

    if (imageElement && imageUrl) {
      const fullImageUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
      imageElement.src = fullImageUrl;
      imageElement.style.display = 'block';
      imageElement.style.opacity = '1';

      if (imageContainer) {
        imageContainer.classList.add('image-loaded');
      }
    }
  }

  showStep(stepNumber) {
    for (let i = 1; i <= stepNumber; i++) {
      const step = this.querySelector(`[js-step="${i}"]`);
      if (step) {
        step.style.display = 'block';
        if (i === stepNumber) {
          step.classList.add('active');
        }
      }
    }
  }

  setupShippingKit() {
    console.log('→ Setting up shipping kit selector');

    const shippingRadios = this.querySelectorAll('[js-shipping-kit]');

    shippingRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          this.shippingKit = radio.value;
          console.log('✓ Shipping kit selected:', this.shippingKit);
          this.updateSummary();

          // Move to next step (custom fields)
          const shippingStepNumber = this.product.options.length + 1;
          this.showStep(shippingStepNumber + 1);
        }
      });
    });

    console.log('✓ Shipping kit selector set up complete');
  }

  setupCustomFields() {
    console.log('→ Setting up custom fields');

    // Bouquet date field
    const bouquetDateField = this.querySelector('[js-bouquet-date]');
    if (bouquetDateField) {
      bouquetDateField.addEventListener('input', () => {
        this.bouquetDate = bouquetDateField.value;
        this.updateSummary();
      });
    }

    // Color correction radio buttons
    const colorRadios = this.querySelectorAll('[js-color-correction]');
    colorRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          this.colorCorrection = radio.value;
          console.log('✓ Color correction:', this.colorCorrection);
          this.updateSummary();
        }
      });
    });

    console.log('✓ Custom fields set up complete');
  }

  getSelections() {
    const shippingKitInput = this.querySelector('input[name="shipping_kit"]:checked');

    const selections = {
      variant: this.selectedVariant,
      shippingKit: shippingKitInput?.value || this.shippingKit,
      shippingKitInput: shippingKitInput,
      bouquetDate: this.bouquetDate,
      colorCorrection: this.colorCorrection
    };

    // Add selected options with their labels
    if (this.hasRealOptions) {
      Object.keys(this.selectedOptions).forEach((key, index) => {
        const optionName = this.product.options[index];
        selections[`option${index + 1}`] = this.selectedOptions[key];
        selections[`option${index + 1}_name`] = optionName;
      });
    }

    return selections;
  }

  updateSummary() {
    const selections = this.getSelections();

    // Update variant option values in summary (only if has real options)
    if (this.hasRealOptions) {
      for (let i = 1; i <= this.product.options.length; i++) {
        const summaryElement = this.querySelector(`[js-summary-option-${i}]`);
        if (summaryElement) {
          summaryElement.textContent = selections[`option${i}`] || '-';
        }
      }
    }

    // Update shipping kit summary
    const shippingSummaryValue = this.querySelector('[js-summary-shipping]');
    if (shippingSummaryValue) {
      const shippingLabels = {
        'label_included': 'Flower Shipping Box + Prepaid Label',
        'supplies_only': 'Supplies Only - I\'ll Purchase My Label',
        'gather_myself': 'No Kit - I\'ll Gather Everything',
        'dropping_off': 'No Kit - Dropping Off'
      };
      shippingSummaryValue.textContent = shippingLabels[selections.shippingKit] || selections.shippingKit || '-';
    }

    // Update bouquet date summary
    const dateSummary = this.querySelector('[js-date-summary]');
    const dateValue = this.querySelector('[js-summary-date]');
    if (dateSummary && dateValue) {
      if (this.bouquetDate) {
        dateSummary.style.display = '';
        dateValue.textContent = this.bouquetDate;
      } else {
        dateSummary.style.display = 'none';
      }
    }

    // Update color correction summary
    const colorSummaryValue = this.querySelector('[js-summary-color]');
    if (colorSummaryValue) {
      colorSummaryValue.textContent = this.colorCorrection;
    }

    // Calculate prices
    let basePrice = 0;
    const extras = [];

    if (this.selectedVariant) {
      basePrice = this.selectedVariant.price / 100;

      // Check for shipping kit extras
      if (selections.shippingKitInput?.dataset.price) {
        const shippingPrice = parseFloat(selections.shippingKitInput.dataset.price);
        if (shippingPrice > 0) {
          const shippingLabels = {
            'label_included': 'Shipping Box + Prepaid Label',
            'supplies_only': 'Supplies Only Kit'
          };
          extras.push({
            label: shippingLabels[selections.shippingKit] || 'Shipping Kit',
            price: shippingPrice
          });
        }
      }
    }

    // Update price display
    const basePriceElement = this.querySelector('[js-base-price]');
    if (basePriceElement) {
      basePriceElement.textContent = this.formatPrice(basePrice);
    }

    // Update extras
    const extrasContainer = this.querySelector('[js-price-extras]');
    if (extrasContainer) {
      extrasContainer.innerHTML = '';
      extras.forEach(extra => {
        const extraDiv = document.createElement('div');
        extraDiv.className = 'price-extra-item';
        extraDiv.innerHTML = `
          <span class="price-extra-label">${extra.label}:</span>
          <span class="price-extra-value">+${this.formatPrice(extra.price)}</span>
        `;
        extrasContainer.appendChild(extraDiv);
      });
    }

    // Calculate and display total
    const extrasTotal = extras.reduce((sum, extra) => sum + extra.price, 0);
    const total = basePrice + extrasTotal;
    const totalPriceElement = this.querySelector('[js-total-price]');
    if (totalPriceElement) {
      totalPriceElement.textContent = this.formatPrice(total);
    }

    // Show summary section
    const summarySection = this.querySelector('[js-summary]');
    if (summarySection && this.selectedVariant) {
      summarySection.style.display = 'block';
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  }

  setupAddToCart() {
    console.log('→ Setting up add to cart');

    const addToCartBtn = this.querySelector('[js-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => {
        this.handleAddToCart();
      });
    }

    console.log('✓ Add to cart set up complete');
  }

  async handleAddToCart() {
    const addToCartBtn = this.querySelector('[js-add-to-cart]');
    const selections = this.getSelections();

    console.log('→ Adding to cart with selections:', selections);

    // Validate selections
    if (!this.selectedVariant) {
      alert('Please complete all selections before adding to cart');
      return;
    }

    if (!this.selectedVariant.available) {
      alert('This variant is currently out of stock');
      return;
    }

    // Validate required fields
    if (!this.bouquetDate) {
      alert('Please enter the date you will be sending your bouquet');
      return;
    }

    // Get quantity
    const quantityInput = this.querySelector('[name="quantity"]');
    const quantity = parseInt(quantityInput?.value || 1);

    // Build components array for pricing adjustments
    const components = [];
    const basePrice = this.selectedVariant.price / 100;

    // Build attributes for the main product component
    const mainProductAttributes = {};

    // Add variant options as attributes
    if (this.hasRealOptions) {
      for (let i = 1; i <= this.product.options.length; i++) {
        if (selections[`option${i}_name`] && selections[`option${i}`]) {
          mainProductAttributes[selections[`option${i}_name`]] = selections[`option${i}`];
        }
      }
    }

    // Add custom fields as attributes
    const shippingLabels = {
      'label_included': 'Flower Shipping Box + Prepaid Label (+$145)',
      'supplies_only': 'Supplies Only - I\'ll Purchase My Label (+$45)',
      'gather_myself': 'No Kit - I\'ll Gather Everything',
      'dropping_off': 'No Kit - Dropping Off'
    };
    mainProductAttributes['Shipping Kit'] = shippingLabels[selections.shippingKit] || selections.shippingKit;
    mainProductAttributes['Bouquet Send Date'] = this.bouquetDate;
    mainProductAttributes['Color Correction'] = this.colorCorrection;

    // Add parent product variant as first component with attributes
    components.push({
      id: this.selectedVariant.id,
      quantity: 1,
      price: basePrice,
      attributes: mainProductAttributes
    });

    // Add Shipping Kit component if selected and has a price
    if (selections.shippingKitInput?.dataset.price && parseFloat(selections.shippingKitInput.dataset.price) > 0 && this.metaobjectsData?.shipping_kit_variant) {
      const shippingKitPrice = parseFloat(selections.shippingKitInput.dataset.price);
      const shippingTypeLabels = {
        'label_included': 'Flower Shipping Box + Prepaid Label',
        'supplies_only': 'Supplies Only Kit'
      };
      components.push({
        id: this.metaobjectsData.shipping_kit_variant,
        quantity: 1,
        price: shippingKitPrice,
        attributes: {
          "Type": shippingTypeLabels[selections.shippingKit] || "Shipping Kit",
          "Shipping Option": shippingTypeLabels[selections.shippingKit] || selections.shippingKit
        }
      });
    }

    // Build line item properties for the main product
    const properties = {};

    // Add _settings with title
    properties['_settings'] = JSON.stringify({
      title: this.metaobjectsData?.product_title || this.product?.title || 'Jewelry Box Bundle'
    });

    // Add components to properties
    if (components.length > 0) {
      properties['_components'] = JSON.stringify(components);
    }

    console.log('→ Line item properties:', properties);
    console.log('→ Components:', components);

    // Disable button
    addToCartBtn.disabled = true;
    addToCartBtn.textContent = 'Adding...';

    // Build request data
    const data = {
      items: [{
        id: this.metaobjectsData?.product_variant || this.selectedVariant.id,
        quantity: quantity,
        properties: properties
      }]
    };

    console.log('→ Request data:', data);

    try {
      // Add the main product
      console.log('→ Adding product to cart');
      const payload = JSON.stringify(data);
      const mainResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload
      });

      if (!mainResponse.ok) {
        throw new Error(`HTTP error! status: ${mainResponse.status}`);
      }

      const mainResult = await mainResponse.json();
      console.log('✓ Product added:', mainResult);

      // Success feedback
      addToCartBtn.textContent = 'Added to Cart!';
      setTimeout(() => {
        addToCartBtn.textContent = 'Add to Cart';
        addToCartBtn.disabled = false;
      }, 2000);

      // Trigger cart drawer to open
      this.openCartDrawer();

    } catch (error) {
      console.error('✗ Failed to add to cart:', error);
      alert('Failed to add to cart. Please try again.');
      addToCartBtn.textContent = 'Add to Cart';
      addToCartBtn.disabled = false;
    }
  }

  openCartDrawer() {
    console.log('→ Opening cart drawer');

    // Dispatch custom events for cart update
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('theme:cart:change'));

    // Try common theme-specific cart drawer triggers
    if (typeof window.theme !== 'undefined' && window.theme.openCartDrawer) {
      window.theme.openCartDrawer();
      console.log('✓ Called window.theme.openCartDrawer()');
    } else if (typeof window.Shopify !== 'undefined' && window.Shopify.theme && window.Shopify.theme.cart) {
      window.Shopify.theme.cart.open();
      console.log('✓ Called window.Shopify.theme.cart.open()');
    }

    // Try to find and click cart drawer toggle
    const selectors = [
      '[data-cart-drawer-toggle]',
      '.cart-drawer-toggle',
      '[data-action="toggle-cart"]',
      '[aria-controls*="cart"]',
      '.header__icon--cart',
      '#cart-icon-bubble',
      'a[href="/cart"]',
      '.cart-count-bubble',
      '[data-cart-icon]',
      'cart-notification-button',
      'cart-drawer-toggle',
      '.cart-link',
      '[data-open-cart]'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`✓ Found cart element: ${selector}`);
        element.click();
        console.log('✓ Clicked cart drawer toggle');
        break;
      }
    }

    // Try to open cart drawer by setting attribute
    const cartDrawer = document.querySelector('cart-drawer, [id*="cart-drawer"], .cart-drawer');
    if (cartDrawer) {
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('active', 'open');
      console.log('✓ Set cart drawer open attribute');
    }

    console.log('✓ Cart drawer triggers dispatched');
  }
}

// Register the custom element
customElements.define('jewelry-box-addon', JewelryBoxAddon);
